/**
 * Invite Landing Page
 * Handles referral code acceptance and event RSVP flow
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  PartyPopper,
  Calendar,
  Clock,
  MapPin,
  Users,
  Ticket,
  Sparkles,
  Loader2,
  AlertCircle,
  CheckCircle,
  LogIn
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatEventDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    weekday: 'long',
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });
};

const formatEventTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
};

export default function InviteLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { session, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [referral, setReferral] = useState(null);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  
  // Fetch referral details
  useEffect(() => {
    const fetchReferral = async () => {
      if (!code) {
        setError('Invalid invite link');
        setLoading(false);
        return;
      }
      
      try {
        const res = await fetch(`${API_URL}/api/referrals/code/${code}`);
        
        if (res.ok) {
          const data = await res.json();
          setReferral(data);
          
          // Fetch event details if referral is valid
          if (data.is_valid && data.event_id) {
            const eventRes = await fetch(`${API_URL}/api/discover/events/${data.event_id}`);
            if (eventRes.ok) {
              const eventData = await eventRes.json();
              setEvent(eventData);
            }
          }
        } else {
          setError('Invite not found or expired');
        }
      } catch (err) {
        console.error('Failed to fetch referral:', err);
        setError('Failed to load invitation');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReferral();
  }, [code]);
  
  // Accept invite and RSVP
  const handleAcceptInvite = async () => {
    if (!session?.access_token) {
      // Store referral code in localStorage and redirect to login
      localStorage.setItem('pendingReferral', code);
      toast.info('Please sign in to accept this invitation');
      navigate('/login');
      return;
    }
    
    setAccepting(true);
    
    try {
      // First RSVP to the event
      const rsvpRes = await fetch(`${API_URL}/api/discover/events/${referral.event_id}/rsvp`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (rsvpRes.ok) {
        const rsvpData = await rsvpRes.json();
        
        // Then accept the referral
        await fetch(`${API_URL}/api/referrals/accept`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            referral_code: code,
            guest_id: rsvpData.guest_id || rsvpData.id
          })
        });
        
        toast.success('Invitation accepted! You\'re on the list.');
        navigate('/my-pass');
      } else {
        const error = await rsvpRes.json();
        toast.error(error.detail || 'Failed to accept invitation');
      }
    } catch (err) {
      console.error('Failed to accept invite:', err);
      toast.error('Something went wrong');
    } finally {
      setAccepting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-violet-400 animate-spin mx-auto mb-4" />
          <p className="text-white/50">Loading invitation...</p>
        </div>
      </div>
    );
  }
  
  if (error || !referral) {
    return (
      <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-rose-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Invite Not Found</h1>
          <p className="text-white/50 mb-6">
            {error || 'This invitation link is invalid or has expired.'}
          </p>
          <Button
            onClick={() => navigate('/discover')}
            className="bg-violet-500 hover:bg-violet-600"
          >
            Discover Events
          </Button>
        </div>
      </div>
    );
  }
  
  if (!referral.is_valid) {
    return (
      <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Invite Expired</h1>
          <p className="text-white/50 mb-6">
            This invitation has already been used or has expired.
          </p>
          <Button
            onClick={() => navigate('/discover')}
            className="bg-violet-500 hover:bg-violet-600"
          >
            Discover Events
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Invite Card */}
        <div className="bg-gradient-to-b from-[#12121e] to-[#0f0f17] rounded-3xl border border-white/[0.08] overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="relative h-40 bg-gradient-to-br from-violet-500/20 via-purple-500/10 to-transparent flex items-center justify-center">
            <PartyPopper className="w-16 h-16 text-violet-400/50" />
            <div className="absolute top-4 left-4">
              <div className="px-3 py-1.5 bg-violet-500/20 backdrop-blur-sm rounded-full border border-violet-500/30">
                <span className="text-xs font-semibold text-violet-300 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  You're Invited!
                </span>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Invitee greeting */}
            {referral.invitee_name && (
              <div className="text-center">
                <p className="text-white/50 text-sm">Hey {referral.invitee_name}!</p>
              </div>
            )}
            
            {/* Event Name */}
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white mb-2">
                {event?.event_name || 'Event'}
              </h1>
              <p className="text-white/50">
                by {event?.organization_name || 'Organization'}
              </p>
            </div>
            
            {/* Event Details */}
            {event && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-xl">
                  <Calendar className="w-5 h-5 text-violet-400" />
                  <span className="text-white">{formatEventDate(event.start_time)}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-xl">
                  <Clock className="w-5 h-5 text-cyan-400" />
                  <span className="text-white">{formatEventTime(event.start_time)}</span>
                </div>
                {event.location && (
                  <div className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-xl">
                    <MapPin className="w-5 h-5 text-emerald-400" />
                    <span className="text-white">{event.location}</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Referral Code */}
            <div className="text-center p-4 bg-white/[0.02] rounded-xl border border-white/[0.06]">
              <p className="text-xs text-white/40 mb-1">Your Invite Code</p>
              <p className="text-xl font-mono font-bold text-violet-400 tracking-widest">
                {referral.referral_code}
              </p>
            </div>
            
            {/* CTA */}
            <Button
              onClick={handleAcceptInvite}
              disabled={accepting}
              className="w-full h-14 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-lg font-semibold"
              data-testid="accept-invite-btn"
            >
              {accepting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Accepting...
                </span>
              ) : session ? (
                <>
                  <Ticket className="w-5 h-5 mr-2" />
                  Accept & Join Event
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign In to Accept
                </>
              )}
            </Button>
            
            {!session && (
              <p className="text-xs text-white/40 text-center">
                You'll need to sign in or create an account to accept this invitation
              </p>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-white/30 text-xs">
            Powered by <span className="text-white/50">CamPassed</span>
          </p>
        </div>
      </div>
    </div>
  );
}
